import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, AlertTriangle, ArrowRight, Mail, Sparkles } from 'lucide-react';
import PacerLogo from '../components/PacerLogo';
import { analyzeStore } from '../utils/analyzer';

// Blocked personal email domains
const BLOCKED_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
  'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
  'gmx.com', 'live.com', 'msn.com', 'me.com', 'inbox.com'
];

// Progress steps for the loading animation
const ANALYSIS_STEPS = [
  { id: 1, label: 'Connecting to store', duration: 5 },
  { id: 2, label: 'Analysing UCP Core Protocol', duration: 10 },
  { id: 3, label: 'Checking Transaction Readiness', duration: 15 },
  { id: 4, label: 'Evaluating Semantic Layer', duration: 20 },
  { id: 5, label: 'Testing Agent Communication', duration: 25 },
  { id: 6, label: 'Verifying Identity & Auth', duration: 30 },
  { id: 7, label: 'Scanning Site-wide Signals', duration: 35 },
  { id: 8, label: 'Analysing Product Pages', duration: 40 },
  { id: 9, label: 'Checking Collection Pages', duration: 45 },
  { id: 10, label: 'Reviewing Discoverable Pages', duration: 50 },
  { id: 11, label: 'Generating recommendations', duration: 55 },
  { id: 12, label: 'Finalising report', duration: 58 },
];

const TOTAL_DURATION = 60; // seconds

const ScanPage = () => {
  const { domain: urlDomain } = useParams();
  const navigate = useNavigate();
  
  // Email capture state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(!urlDomain);
  
  // Analysis state
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(TOTAL_DURATION);
  const [loadingStatus, setLoadingStatus] = useState('Initialising scan...');
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [targetDomain, setTargetDomain] = useState(urlDomain || '');

  // Extract domain from email
  const extractDomainFromEmail = (emailAddress) => {
    const parts = emailAddress.split('@');
    if (parts.length === 2) {
      return parts[1].toLowerCase();
    }
    return null;
  };

  // Validate email
  const validateEmail = (emailAddress) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailAddress)) {
      return 'Please enter a valid email address';
    }
    
    const domain = extractDomainFromEmail(emailAddress);
    if (domain && BLOCKED_EMAIL_DOMAINS.includes(domain)) {
      return 'Please use your business email address';
    }
    
    return null;
  };

  // Handle email submission
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    
    const validationError = validateEmail(email);
    if (validationError) {
      setEmailError(validationError);
      return;
    }
    
    const domain = extractDomainFromEmail(email);
    if (domain) {
      setTargetDomain(domain);
      setShowEmailForm(false);
      setIsLoading(true);
    }
  };

  // Get current step based on progress
  const getCurrentStep = () => {
    const currentTime = (progress / 100) * TOTAL_DURATION;
    for (let i = ANALYSIS_STEPS.length - 1; i >= 0; i--) {
      if (currentTime >= ANALYSIS_STEPS[i].duration) {
        return ANALYSIS_STEPS[i];
      }
    }
    return ANALYSIS_STEPS[0];
  };

  // Progress simulation effect
  useEffect(() => {
    if (!isLoading || results) return;
    
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 95) return prev;
        const increment = Math.random() * 2 + 0.5;
        return Math.min(prev + increment, 95);
      });
      
      setTimeRemaining(prev => {
        if (prev <= 1) return 0;
        return prev - 1;
      });
      
      const step = getCurrentStep();
      if (step) {
        setLoadingStatus(step.label);
      }
    }, 1000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, results]);

  // Auto-start analysis when we have a domain (either from URL or email)
  useEffect(() => {
    if (!targetDomain || showEmailForm || results) return;

    const runAnalysis = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setProgress(0);
        setTimeRemaining(TOTAL_DURATION);
        
        // Use the captured email or a placeholder
        const userEmail = email || `scan@${targetDomain}`;
        
        const analysisResults = await analyzeStore(targetDomain, userEmail, (status) => {
          if (status) setLoadingStatus(status);
        });
        
        // Complete the progress
        setProgress(100);
        setTimeRemaining(0);
        setLoadingStatus('Complete!');
        
        setResults(analysisResults);
        
        if (analysisResults.analysisId) {
          setAnalysisId(analysisResults.analysisId);
        }
      } catch (err) {
        console.error('Analysis error:', err);
        setError(err.message || 'Unable to complete analysis. Please try again.');
        setIsLoading(false);
      }
    };

    runAnalysis();
  }, [targetDomain, showEmailForm, email, results]);

  // Redirect to results when analysis completes
  useEffect(() => {
    if (results && analysisId) {
      const timer = setTimeout(() => {
        navigate(`/report/${analysisId}`);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [results, analysisId, navigate]);

  const currentStep = getCurrentStep();

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f0] font-['TWK_Everett']">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <PacerLogo />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 md:pt-32 pb-16 px-4 md:px-6 flex items-center justify-center min-h-screen">
        <div className="max-w-xl w-full text-center">
          
          {/* Email Capture Form */}
          {showEmailForm && !error && (
            <div className="bg-[#111111] rounded-2xl p-8 border border-white/5">
              <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-emerald-400" />
              </div>
              
              <h1 className="text-2xl md:text-3xl font-light mb-3">
                Get Your AI Readiness Score
              </h1>
              <p className="text-[#f5f5f0]/50 mb-8 max-w-md mx-auto">
                Enter your business email and we'll instantly analyse your store's 
                readiness for AI-powered commerce.
              </p>
              
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#f5f5f0]/30" />
                  <Input
                    type="email"
                    placeholder="you@yourcompany.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError('');
                    }}
                    className="pl-12 h-14 bg-[#0a0a0a] border-white/10 text-[#f5f5f0] placeholder:text-[#f5f5f0]/30 rounded-xl text-base"
                    data-testid="scan-email-input"
                  />
                </div>
                
                {emailError && (
                  <p className="text-red-400 text-sm text-left">{emailError}</p>
                )}
                
                <Button 
                  type="submit"
                  className="w-full h-14 bg-[#f5f5f0] text-[#0a0a0a] hover:bg-[#e5e5e0] rounded-xl text-base font-medium"
                  data-testid="scan-submit-btn"
                >
                  Analyse My Store
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </form>
              
              <p className="text-xs text-[#f5f5f0]/30 mt-6">
                We'll analyse the domain from your email address. 
                Your results will be ready in under 60 seconds.
              </p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-[#111111] rounded-2xl p-8 border border-red-500/20">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              <h1 className="text-2xl font-medium mb-3">Analysis Failed</h1>
              <p className="text-[#f5f5f0]/50 mb-6">{error}</p>
              <Button 
                onClick={() => {
                  setError(null);
                  setShowEmailForm(true);
                  setTargetDomain('');
                  setEmail('');
                }}
                className="bg-[#f5f5f0] text-[#0a0a0a] hover:bg-[#e5e5e0]"
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Loading State */}
          {isLoading && !showEmailForm && !error && (
            <div className="bg-[#111111] rounded-2xl p-8 border border-white/5">
              {/* Domain Being Scanned */}
              <div className="mb-8">
                <p className="text-sm text-[#f5f5f0]/40 mb-2">Scanning</p>
                <h1 className="text-2xl md:text-3xl font-light">{targetDomain}</h1>
              </div>

              {/* Progress Circle */}
              <div className="relative w-40 h-40 mx-auto mb-8">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    fill="none"
                    stroke="rgba(245, 245, 240, 0.1)"
                    strokeWidth="8"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    fill="none"
                    stroke="url(#progressGradient)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 70}`}
                    strokeDashoffset={`${2 * Math.PI * 70 * (1 - progress / 100)}`}
                    className="transition-all duration-1000 ease-out"
                  />
                  <defs>
                    <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#34d399" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-light">{Math.round(progress)}%</span>
                  <span className="text-xs text-[#f5f5f0]/40 mt-1">
                    {timeRemaining > 0 ? `~${timeRemaining}s remaining` : 'Finalising...'}
                  </span>
                </div>
              </div>

              {/* Current Step */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
                <span className="text-sm text-[#f5f5f0]/70">{loadingStatus}</span>
              </div>

              {/* Step Progress Dots */}
              <div className="flex justify-center gap-1.5">
                {ANALYSIS_STEPS.map((step) => (
                  <div
                    key={step.id}
                    className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                      currentStep && step.id <= currentStep.id
                        ? 'bg-emerald-400'
                        : 'bg-[#f5f5f0]/10'
                    }`}
                  />
                ))}
              </div>

              {/* Completion Message */}
              {progress === 100 && (
                <div className="mt-8 flex items-center justify-center gap-2 text-emerald-400">
                  <span>Redirecting to your report</span>
                  <ArrowRight className="w-4 h-4 animate-pulse" />
                </div>
              )}
            </div>
          )}

          {/* Info Text */}
          {isLoading && !error && (
            <p className="text-xs text-[#f5f5f0]/30 mt-6 max-w-md mx-auto">
              We're analysing {targetDomain} for AI agent readiness across 10 categories 
              including UCP protocol support, transaction capabilities, and semantic structure.
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default ScanPage;
